const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const getStringValue = (value: Record<string, unknown>, key: string) => {
    const maybeValue = value[key];
    return typeof maybeValue === 'string' && maybeValue.trim().length > 0
        ? maybeValue
        : undefined;
};

const getStatusValue = (value: Record<string, unknown>) => {
    const status = value.status ?? value.originalStatus;
    return typeof status === 'string' || typeof status === 'number'
        ? status
        : undefined;
};

const standardErrorKeys = new Set([
    'data',
    'error',
    'message',
    'name',
    'originalStatus',
    'stack',
    'status',
]);

const getAdditionalProperties = (value: Record<string, unknown>) => {
    const additionalProperties: Record<string, unknown> = {};

    Object.entries(value).forEach(([key, propertyValue]) => {
        if (!standardErrorKeys.has(key) && propertyValue !== undefined) {
            additionalProperties[key] = propertyValue;
        }
    });

    if (Object.keys(additionalProperties).length === 0) {
        return undefined;
    }

    try {
        return JSON.stringify(additionalProperties);
    } catch {
        return '[Unserializable properties]';
    }
};

const appendErrorDetails = (message: string, value: Record<string, unknown>) => {
    const stack = getStringValue(value, 'stack');
    const additionalProperties = getAdditionalProperties(value);
    const details = [
        stack ? `stack: ${stack}` : undefined,
        additionalProperties ? `properties: ${additionalProperties}` : undefined,
    ].filter(Boolean);

    return details.length > 0
        ? `${message} | ${details.join(' | ')}`
        : message;
};

const getDataMessage = (data: unknown): string | undefined => {
    if (typeof data === 'string' && data.trim().length > 0) {
        return data;
    }

    if (!isRecord(data)) {
        return undefined;
    }

    const message = getStringValue(data, 'message')
        ?? getStringValue(data, 'error')
        ?? getStringValue(data, 'title')
        ?? getStringValue(data, 'detail');

    if (message) {
        return message;
    }

    try {
        return JSON.stringify(data);
    } catch {
        return undefined;
    }
};

export const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
        return appendErrorDetails(
            error.message || error.name || 'Error',
            error as unknown as Record<string, unknown>
        );
    }

    if (typeof error === 'string') {
        return error.trim().length > 0 ? error : 'Unknown error';
    }

    if (isRecord(error)) {
        const status = getStatusValue(error);
        const message = getStringValue(error, 'message') ?? getStringValue(error, 'error');
        if (message) {
            const errorMessage = status !== undefined
                ? `${status}: ${message}`
                : message;
            return appendErrorDetails(errorMessage, error);
        }

        const dataMessage = getDataMessage(error.data);

        if (status !== undefined && dataMessage) {
            return appendErrorDetails(`${status}: ${dataMessage}`, error);
        }

        if (dataMessage) {
            return appendErrorDetails(dataMessage, error);
        }

        if (status !== undefined) {
            return appendErrorDetails(`Request failed with status ${status}`, error);
        }

        try {
            const serializedError = JSON.stringify(error);
            if (serializedError && serializedError !== '{}') {
                return serializedError;
            }
        } catch {
            return 'Unserializable error';
        }
    }

    if (error == null) {
        return 'Unknown error';
    }

    return String(error);
};
